import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface PickerOption {
  value: string;
  label: string;
  searchText?: string;
  icon: ReactNode;
  content?: ReactNode;
  group?: string;
  count?: number;
}

interface SearchablePickerProps {
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  label: string;
  dialogLabel: string;
  searchLabel: string;
  emptyMessage?: string;
}

export function SearchablePicker({ value, onChange, options, label, dialogLabel, searchLabel, emptyMessage = 'No matches found. Try another search.' }: SearchablePickerProps) {
  const [open, setOpen] = useState(false);
  const [opensAbove, setOpensAbove] = useState(false);
  const [listHeight, setListHeight] = useState(360);
  const [panelOffset, setPanelOffset] = useState(0);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelId = useId();
  const selected = options.find((option) => option.value === value);
  const filtered = options.filter((option) => (option.searchText ?? option.label).toLowerCase().includes(query.trim().toLowerCase()));
  const groups = new Map<string, PickerOption[]>();
  for (const option of filtered) {
    const group = option.group ?? '';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(option);
  }

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function closeOutside(event: PointerEvent): void {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  function choose(nextValue: string): void {
    onChange(nextValue);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function showPicker(): void {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceAbove = rect.top - 84;
      const spaceBelow = window.innerHeight - rect.bottom - 16;
      const above = spaceBelow < 420 && spaceAbove > spaceBelow;
      const panelWidth = Math.min(380, window.innerWidth - 40);
      setOpensAbove(above);
      setListHeight(Math.max(100, Math.min(360, (above ? spaceAbove : spaceBelow) - 80)));
      setPanelOffset(Math.max(20, Math.min(rect.left, window.innerWidth - panelWidth - 20)) - rect.left);
    }
    setQuery('');
    setOpen(true);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    const buttons = [...(rootRef.current?.querySelectorAll<HTMLButtonElement>('[data-picker-option]') ?? [])];
    if (event.key === 'Enter' && event.target === inputRef.current) {
      // Searching inside a form must never submit it, even when there are no matches.
      event.preventDefault();
      buttons[0]?.click();
      return;
    }
    if (!buttons.length) return;
    const index = buttons.indexOf(event.target as HTMLButtonElement);
    let nextIndex: number;
    if (event.key === 'ArrowDown') nextIndex = (index + 1) % buttons.length;
    else if (event.key === 'ArrowUp') nextIndex = index <= 0 ? buttons.length - 1 : index - 1;
    else if (event.key === 'Home' && index >= 0) nextIndex = 0;
    else if (event.key === 'End' && index >= 0) nextIndex = buttons.length - 1;
    else return;
    event.preventDefault();
    buttons[nextIndex].focus();
  }

  return (
    <div className="filter-picker" ref={rootRef} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      <button
        ref={triggerRef}
        className="filter-picker-trigger"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${label}: ${selected?.label ?? value}`}
        title={selected?.label ?? value}
        onClick={() => open ? setOpen(false) : showPicker()}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            showPicker();
          }
        }}
      >
        <span className="filter-picker-icon" aria-hidden="true">{selected?.icon}</span>
        <span className="filter-picker-value">{selected?.label ?? value}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className={`filter-picker-panel${opensAbove ? ' opens-above' : ''}`} style={{ left: panelOffset }} id={panelId} role="dialog" aria-label={dialogLabel} onKeyDown={handleKeyDown}>
          <label className="filter-picker-search">
            <Search size={17} aria-hidden="true" />
            <span className="sr-only">{searchLabel}</span>
            <input ref={inputRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`${searchLabel}…`} autoComplete="off" />
          </label>
          <div className="filter-picker-options" style={{ maxHeight: `min(${listHeight}px, 45dvh)` }}>
            {[...groups].map(([group, items]) => (
              <div className="filter-picker-group" role={group ? 'group' : undefined} aria-label={group || undefined} key={group}>
                {group && <p aria-hidden="true">{group}</p>}
                {items.map((option) => (
                  <button className="filter-picker-option" type="button" key={option.value} data-picker-option aria-pressed={value === option.value} onClick={() => choose(option.value)}>
                    <span className="filter-picker-icon" aria-hidden="true">{option.icon}</span>
                    <span className="filter-picker-name">{option.content ?? option.label}</span>
                    <span className="filter-picker-count">{option.count !== undefined && <>{option.count.toLocaleString()}<span className="sr-only"> profiles</span></>}</span>
                    <span className="filter-picker-check" aria-hidden="true">{value === option.value && <Check size={16} />}</span>
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && <p className="filter-picker-empty" role="status">{emptyMessage}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
